/* eslint-disable complexity */
import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Copy } from 'lucide-react'
import { ListPage } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getJSON } from '@/lib/api'
import { formatMoneyCents } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { EventRecord } from '@/pages/event-types'
import { useViewOnly } from '@/components/templates/app-shell'
import { OwnerFilter } from '@/components/OwnerFilter'
import { OwnedChip } from '@/components/OwnedChip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const STATUS_FILTERS = [
  ['all', 'All'],
  ['upcoming', 'Upcoming'],
  ['drafts', 'Drafts'],
  ['past', 'Past'],
  ['cancelled', 'Cancelled'],
] as const

export function EventsListPage({
  onNew,
  onOpen,
  onDuplicate,
  duplicateError,
  duplicating = false,
}: {
  onNew: () => void
  onOpen: (event: EventRecord) => void
  onDuplicate: (event: EventRecord) => void
  duplicateError?: string
  duplicating?: boolean
}) {
  const viewOnly = useViewOnly()
  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState('all')
  const [sort, setSort] = React.useState('date')
  const [direction, setDirection] = React.useState<'ascending' | 'descending'>('descending')
  const [chapterId, setChapterId] = React.useState('')
  const [owner, setOwner] = React.useState<string | undefined>()
  const chaptersQuery = useQuery({
    queryKey: ['event-chapters'],
    queryFn: () => getJSON<{ chapters: { id: number; name: string }[] }>('/api/v1/chapters'),
    retry: false,
  })
  const queryString = new URLSearchParams({
    ...(search ? { search } : {}),
    ...(filter === 'all' ? {} : { filter }),
    ...(chapterId ? { chapter_id: chapterId } : {}),
    ...(owner ? { owner } : {}),
    sort,
    direction,
  }).toString()
  const query = useQuery({
    queryKey: ['events', search, filter, chapterId, owner, sort, direction],
    queryFn: () => getJSON<{ events: EventRecord[] }>(`/api/v1/events?${queryString}`),
    retry: false,
  })
  const toggleSort = (column: string) => {
    if (sort === column)
      setDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'))
    else {
      setSort(column)
      setDirection('ascending')
    }
  }

  return (
    <>
      {duplicateError && (
        <div className="mx-auto w-full max-w-[1200px] px-6 pt-6">
          <Alert>{duplicateError}</Alert>
        </div>
      )}
      <ListPage
        title="Events"
        primaryAction={{ label: 'New event', onClick: onNew }}
        search={{ value: search, onChange: setSearch, placeholder: 'Search events…' }}
        toolbar={
          <div className="flex flex-wrap items-center gap-1.5" aria-label="Filter events">
            {STATUS_FILTERS.map(([value, label]) => (
              <Button
                key={value}
                type="button"
                variant="outline"
                size="sm"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={cn(
                  'h-8 rounded-full px-3 text-caption font-normal',
                  filter === value && 'border-foreground/20 bg-accent font-medium text-foreground',
                )}
              >
                {label}
              </Button>
            ))}
            <ChapterFilter
              chapters={chaptersQuery.data?.chapters ?? []}
              value={chapterId}
              onChange={setChapterId}
            />
            <OwnerFilter value={owner} onChange={setOwner} />
          </div>
        }
        table={{
          rows: query.data?.events ?? [],
          state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
          getRowId: (event) => event.id,
          getRowLabel: (event) => event.name,
          onRowOpen: onOpen,
          emptyTitle: search || filter !== 'all' ? 'No matching events' : 'No events yet.',
          emptyVariant: search || filter !== 'all' ? 'default' : 'quiet',
          columns: eventColumns({
            sort,
            direction,
            toggleSort,
            onDuplicate,
            duplicating,
            viewOnly,
          }),
        }}
      />
    </>
  )
}

function ChapterFilter({
  chapters,
  value,
  onChange,
}: {
  chapters: { id: number; name: string }[]
  value: string
  onChange: (value: string) => void
}) {
  if (chapters.length === 0) return null
  return (
    <Select value={value || 'all'} onValueChange={(next) => onChange(next === 'all' ? '' : next)}>
      <SelectTrigger aria-label="Filter events by chapter" className="h-8 w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All chapters</SelectItem>
        {chapters.map((chapter) => (
          <SelectItem key={chapter.id} value={String(chapter.id)}>
            {chapter.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function eventColumns({
  sort,
  direction,
  toggleSort,
  onDuplicate,
  duplicating,
  viewOnly,
}: {
  sort: string
  direction: 'ascending' | 'descending'
  toggleSort: (column: string) => void
  onDuplicate: (event: EventRecord) => void
  duplicating: boolean
  viewOnly: boolean
}) {
  const sortable = (column: string) => ({
    active: sort === column,
    direction,
    onToggle: () => toggleSort(column),
  })
  return [
    {
      id: 'name',
      header: 'Event',
      cell: (event: EventRecord) => (
        <div className="min-w-52">
          <p className="text-body-sm font-medium text-foreground">
            {event.name} <OwnedChip owner={event.owner} />
          </p>
          {event.location && <p className="text-caption text-muted-foreground">{event.location}</p>}
          {event.chapter && <Badge variant="neutral">{event.chapter.name}</Badge>}
        </div>
      ),
    },
    {
      id: 'date',
      header: 'Date',
      sort: sortable('date'),
      cell: (event: EventRecord) => (
        <span
          className={cn(
            'whitespace-nowrap text-caption',
            event.upcoming ? 'font-medium text-primary' : 'text-muted-foreground',
          )}
          data-date-accent={event.upcoming ? 'upcoming' : 'past'}
        >
          {localStartLabel(event)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sort: sortable('status'),
      cell: (event: EventRecord) => (
        <Badge variant={event.status === 'published' ? 'success' : 'neutral'}>
          {titleize(event.status)}
        </Badge>
      ),
    },
    {
      id: 'orders',
      header: 'Orders',
      sort: sortable('orders'),
      cell: (event: EventRecord) => <span className="tabular-nums">{event.order_count ?? 0}</span>,
    },
    {
      id: 'revenue',
      header: 'Revenue',
      headerClassName: 'text-right',
      className: 'text-right',
      sort: sortable('revenue'),
      cell: (event: EventRecord) => (
        <span className="whitespace-nowrap font-semibold tabular-nums text-foreground">
          {event.revenue ?? formatMoneyCents(0, event.currency ?? 'USD')}
        </span>
      ),
    },
    {
      id: 'duplicate',
      header: <span className="sr-only">Duplicate</span>,
      headerClassName: 'w-12',
      className: 'w-12 py-1 text-right',
      cell: (event: EventRecord) =>
        viewOnly ? null : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 px-0"
            aria-label={`Duplicate ${event.name}`}
            title={`Duplicate ${event.name}`}
            disabled={duplicating}
            onClick={(clickEvent) => {
              clickEvent.stopPropagation()
              onDuplicate(event)
            }}
            onKeyDown={(keyEvent) => keyEvent.stopPropagation()}
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Button>
        ),
    },
  ]
}

function localStartLabel(event: EventRecord) {
  if (!event.date || !event.time || !event.time_zone_label) return 'Dates not set'

  return `${event.date} · ${event.time} ${event.time_zone_label}`
}

function titleize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
